import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import { useRef, useEffect, useState } from "react";
import RadioGroup from "../AddInvestmentModal/fields/RadioGroup";
import ComputedField from "../AddInvestmentModal/fields/ComputedField";
import {
  Button,
  CalendarInput,
  Checkbox,
  FullscreenModal,
  LargeSwitch,
  SelectComponent,
  Textarea,
} from "shared";
import { Input, DropFileInput } from "shared";
import {
  useUpdateInvestment,
  useUploadInvestmentFile,
  useAttachInvestmentFile,
  useDeleteInvestmentFile,
} from "hooks/investments";
import { useGetCountriesCurrency } from "hooks/countries";
import { useGetMe } from "hooks/auth";
import { InvestmentItem } from "../../types";

interface EditInvestmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: InvestmentItem | null;
  slug: string;
}

// ── treasury_bonds ───────────────────────────────────────────────────────────

const INTEREST_TYPE_OPTIONS = [
  { value: "fixed", label: "Stałe" },
  { value: "variable", label: "Zmienne" },
  { value: "periodically_fixed", label: "Okresowo-stałe" },
];

const treasuryBondsSchema = Yup.object().shape({
  quantity: Yup.string()
    .required("To pole jest wymagane")
    .test("is-positive", "Wartość musi być większa od 0", (val) => {
      if (val === null || val === undefined || val === "") return false;
      return !isNaN(Number(val)) && Number(val) > 0;
    }),
  currencyUuid: Yup.string().required("Waluta jest wymagana"),
  primaryValue: Yup.string()
    .required("To pole jest wymagane")
    .test("is-positive", "Wartość musi być większa od 0", (val) => {
      if (val === null || val === undefined || val === "") return false;
      return !isNaN(Number(val)) && Number(val) > 0;
    }),
  interestRate: Yup.string()
    .required("To pole jest wymagane")
    .test("is-non-negative", "Wartość nie może być ujemna", (val) => {
      if (val === null || val === undefined || val === "") return true;
      return !isNaN(Number(val)) && Number(val) >= 0;
    }),
  interestType: Yup.string().notRequired(),
  additionalInfo: Yup.string().notRequired(),
  contractFile: Yup.mixed().nullable(),
  relianceCanView: Yup.boolean(),
});

interface TreasuryBondsValues {
  quantity: string;
  currencyUuid: string;
  primaryValue: string;
  interestRate: string;
  interestType: string;
  additionalInfo: string;
  contractFile?: File[] | null;
  relianceCanView: boolean;
}

const TreasuryBondsForm = ({
  item,
  onSubmit,
  onCancel,
  isPending,
  currencyOptions,
  onRemovedContract,
}: {
  item: InvestmentItem;
  onSubmit: (values: TreasuryBondsValues) => void;
  onCancel: () => void;
  isPending: boolean;
  currencyOptions?: any[];
  onRemovedContract?: (file: { url: string }) => void;
}) => {
  const existingContractFiles = (item.files ?? [])
    .filter((f: any) => f.role === "contract")
    .map((f: any) => ({ url: f.file.url, name: f.file.name }));

  const initialValues: TreasuryBondsValues = {
    quantity: String(item.quantity ?? ""),
    currencyUuid: (item as any).currency?.uuid ?? "",
    primaryValue: String(item.primaryValue ?? ""),
    interestRate: String(item.details?.interestRate ?? ""),
    interestType: String(item.details?.interestRateType ?? ""),
    additionalInfo: item.additionalInfo ?? "",
    contractFile: null,
    relianceCanView: item.relianceCanView ?? false,
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={treasuryBondsSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {(formik) => (
        <Form>
          <div className="modal-form__fields">
            <Field
              as={Input}
              name="quantity"
              label="Ilość sztuk"
              type="number"
              step="1"
              error={
                (formik.touched.quantity || formik.submitCount > 0) &&
                formik.errors.quantity
                  ? formik.errors.quantity
                  : undefined
              }
            />

            <Field
              as={SelectComponent}
              name="currencyUuid"
              label="Waluta"
              options={currencyOptions ?? []}
              isSearchable={false}
              error={
                (formik.touched.currencyUuid || formik.submitCount > 0) &&
                formik.errors.currencyUuid
                  ? formik.errors.currencyUuid
                  : undefined
              }
            />

            <Field
              as={Input}
              name="primaryValue"
              label="Koszt nabycia"
              type="number"
              step="any"
              error={
                (formik.touched.primaryValue || formik.submitCount > 0) &&
                formik.errors.primaryValue
                  ? formik.errors.primaryValue
                  : undefined
              }
            />

            <Field
              as={Input}
              name="interestRate"
              label="Wysokość oprocentowania"
              type="number"
              step="any"
              suffix="%"
              error={
                (formik.touched.interestRate || formik.submitCount > 0) &&
                formik.errors.interestRate
                  ? formik.errors.interestRate
                  : undefined
              }
            />

            <Field
              as={SelectComponent}
              name="interestType"
              label="Rodzaj oprocentowania"
              options={INTEREST_TYPE_OPTIONS}
              isSearchable={false}
              optional
            />

            <Field
              component={Textarea}
              name="additionalInfo"
              label="Dodatkowe informacje"
              optional
            />

            <Field
              as={DropFileInput}
              name="contractFile"
              label="Umowy"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              optional
              multiple={10}
              existingFiles={existingContractFiles}
              onServerFileRemoved={onRemovedContract}
            />

            <Field
              as={Checkbox}
              name="relianceCanView"
              label="Udzielam zgody, by pracownicy firmy Reliance mieli dostęp do historii moich transakcji związanych z tym aktywem."
              checked={formik.values.relianceCanView}
              onChange={() =>
                formik.setFieldValue(
                  "relianceCanView",
                  !formik.values.relianceCanView,
                )
              }
            />
          </div>

          <div className="modal-buttons">
            <Button
              type="button"
              className="button--stroke button--rounded"
              onClick={onCancel}
              label="Anuluj"
            />
            <Button
              type="submit"
              className="button--black button--rounded"
              onClick={() => {}}
              label={isPending ? "Zapisywanie..." : "Zapisz"}
              isLoading={isPending}
            />
          </div>
        </Form>
      )}
    </Formik>
  );
};

// ── crypto ───────────────────────────────────────────────────────────────────

const cryptoSchema = Yup.object().shape({
  name: Yup.string().required("To pole jest wymagane"),
  additionalInfo: Yup.string().notRequired(),
  relianceCanView: Yup.boolean(),
});

interface CryptoValues {
  name: string;
  additionalInfo: string;
  relianceCanView: boolean;
}

const CryptoForm = ({
  item,
  onSubmit,
  onCancel,
  isPending,
}: {
  item: InvestmentItem;
  onSubmit: (values: CryptoValues) => void;
  onCancel: () => void;
  isPending: boolean;
}) => {
  const initialValues: CryptoValues = {
    name: item.name ?? "",
    additionalInfo: item.additionalInfo ?? "",
    relianceCanView: item.relianceCanView ?? false,
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={cryptoSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {(formik) => (
        <Form>
          <div className="modal-form__fields">
            <Field
              as={Input}
              name="name"
              label="Nazwa"
              type="text"
              error={
                (formik.touched.name || formik.submitCount > 0) &&
                formik.errors.name
                  ? formik.errors.name
                  : undefined
              }
            />

            <Field
              component={Textarea}
              name="additionalInfo"
              label="Dodatkowe informacje"
              optional
            />

            <Field
              as={Checkbox}
              name="relianceCanView"
              label="Udzielam zgody, by pracownicy firmy Reliance mieli dostęp do historii moich transakcji związanych z tym aktywem."
              checked={formik.values.relianceCanView}
              onChange={() =>
                formik.setFieldValue(
                  "relianceCanView",
                  !formik.values.relianceCanView,
                )
              }
            />
          </div>

          <div className="modal-buttons">
            <Button
              type="button"
              className="button--stroke button--rounded"
              onClick={onCancel}
              label="Anuluj"
            />
            <Button
              type="submit"
              className="button--black button--rounded"
              onClick={() => {}}
              label={isPending ? "Zapisywanie..." : "Zapisz"}
              isLoading={isPending}
            />
          </div>
        </Form>
      )}
    </Formik>
  );
};

// ── currencies ───────────────────────────────────────────────────────────────

const currenciesSchema = Yup.object().shape({
  additionalInfo: Yup.string().notRequired(),
  relianceCanView: Yup.boolean(),
});

interface CurrenciesValues {
  additionalInfo: string;
  relianceCanView: boolean;
}

const CurrenciesForm = ({
  item,
  onSubmit,
  onCancel,
  isPending,
}: {
  item: InvestmentItem;
  onSubmit: (values: CurrenciesValues) => void;
  onCancel: () => void;
  isPending: boolean;
}) => {
  const initialValues: CurrenciesValues = {
    additionalInfo: item.additionalInfo ?? "",
    relianceCanView: item.relianceCanView ?? false,
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={currenciesSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {(formik) => (
        <Form>
          <div className="modal-form__fields">
            <Field
              component={Textarea}
              name="additionalInfo"
              label="Dodatkowe informacje"
              optional
            />

            <Field
              as={Checkbox}
              name="relianceCanView"
              label="Udzielam zgody, by pracownicy firmy Reliance mieli dostęp do historii moich transakcji związanych z tym aktywem."
              checked={formik.values.relianceCanView}
              onChange={() =>
                formik.setFieldValue(
                  "relianceCanView",
                  !formik.values.relianceCanView,
                )
              }
            />
          </div>

          <div className="modal-buttons">
            <Button
              type="button"
              className="button--stroke button--rounded"
              onClick={onCancel}
              label="Anuluj"
            />
            <Button
              type="submit"
              className="button--black button--rounded"
              onClick={() => {}}
              label={isPending ? "Zapisywanie..." : "Zapisz"}
              isLoading={isPending}
            />
          </div>
        </Form>
      )}
    </Formik>
  );
};

const DURATION_OPTIONS = [
  { value: "indefinite", label: "Bezterminowo" },
  { value: "specific_date", label: "Dokładna data zakończenia" },
];

const PREMIUM_PAYMENT_OPTIONS = [
  { value: "monthly", label: "Miesięczne" },
  { value: "quarterly", label: "Kwartalne" },
  { value: "yearly", label: "Roczne" },
  { value: "at_end_of_year", label: "Na koniec roku" },
];

const investmentPolicySchema = Yup.object().shape({
  name: Yup.string().required("To pole jest wymagane"),
  currencyUuid: Yup.string().required("To pole jest wymagane"),
  contractDate: Yup.string().notRequired(),
  durationDate: Yup.string().notRequired(),
  durationDateValue: Yup.string().when("durationDate", {
    is: "specific_date",
    then: (schema) => schema.required("To pole jest wymagane"),
    otherwise: (schema) => schema.notRequired(),
  }),
  premiumPayment: Yup.string().notRequired(),
  additionalInfo: Yup.string().notRequired(),
  relianceCanView: Yup.boolean(),
  contractFile: Yup.mixed().nullable(),
});

interface InvestmentPolicyValues {
  name: string;
  currencyUuid: string;
  contractDate: string;
  durationDate: string;
  durationDateValue: string;
  premiumPayment: string;
  additionalInfo: string;
  relianceCanView: boolean;
  contractFile: File[] | null;
}

const InvestmentPolicyForm = ({
  item,
  onSubmit,
  onCancel,
  isPending,
  currencyOptions,
  onRemovedContract,
}: {
  item: InvestmentItem;
  onSubmit: (values: InvestmentPolicyValues) => void;
  onCancel: () => void;
  isPending: boolean;
  currencyOptions: any[];
  onRemovedContract: (file: { name: string; url: string }) => void;
}) => {
  const contractDate = item.details?.contractDate;
  const durationDateRaw = item.details?.durationDate;
  const premiumPayment = item.details?.premiumPayment;

  let durationDate = "";
  let durationDateValue = "";

  if (durationDateRaw === null || durationDateRaw === undefined) {
    durationDate = "indefinite";
  } else if (typeof durationDateRaw === "string" && durationDateRaw !== "") {
    const d = new Date(durationDateRaw);
    if (
      !isNaN(d.getTime()) &&
      d.getFullYear() === 2000 &&
      d.getMonth() === 0 &&
      d.getDate() === 1
    ) {
      durationDate = "";
    } else if (!isNaN(d.getTime())) {
      durationDate = "specific_date";
      durationDateValue = durationDateRaw;
    }
  }

  const initialValues: InvestmentPolicyValues = {
    name: item.name ?? "",
    currencyUuid: item.currencyUuid ?? "",
    contractDate: contractDate ? String(contractDate) : "",
    durationDate,
    durationDateValue,
    premiumPayment: premiumPayment ? String(premiumPayment) : "",
    additionalInfo: item.additionalInfo ?? "",
    relianceCanView: item.relianceCanView ?? false,
    contractFile: null,
  };

  const existingContractFiles = (item.files ?? []).filter(
    (f: any) => f.role === "contract",
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={investmentPolicySchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {(formik) => (
        <Form>
          <div className="modal-form__fields">
            <Field
              as={Input}
              name="name"
              label="Nazwa"
              type="text"
              error={
                (formik.touched.name || formik.submitCount > 0) &&
                formik.errors.name
                  ? formik.errors.name
                  : undefined
              }
            />

            <Field
              as={SelectComponent}
              name="currencyUuid"
              label="Waluta"
              options={currencyOptions}
              isSearchable={true}
              error={
                (formik.touched.currencyUuid || formik.submitCount > 0) &&
                formik.errors.currencyUuid
                  ? formik.errors.currencyUuid
                  : undefined
              }
            />

            <Field
              type="date"
              name="contractDate"
              label="Data zawarcia"
              optional
              component={CalendarInput}
              errors={formik.errors}
            />

            <Field
              as={SelectComponent}
              name="durationDate"
              label="Czas trwania"
              options={DURATION_OPTIONS}
              isSearchable={false}
              optional
            />

            {formik.values.durationDate === "specific_date" && (
              <Field
                type="date"
                name="durationDateValue"
                label="Data zakończenia"
                component={CalendarInput}
                errors={formik.errors}
              />
            )}

            <Field
              as={SelectComponent}
              name="premiumPayment"
              label="Składki"
              options={PREMIUM_PAYMENT_OPTIONS}
              isSearchable={false}
              optional
            />

            <Field
              component={Textarea}
              name="additionalInfo"
              label="Dodatkowe informacje"
              optional
            />

            <Field
              label="Umowa"
              name="contractFile"
              id="contractFile"
              as={DropFileInput}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              optional
              multiple={10}
              existingFiles={existingContractFiles.map((f: any) => ({
                name: f.file?.name || "Plik",
                url: f.file?.url || "",
              }))}
              onServerFileRemoved={onRemovedContract}
            />

            <Field
              as={Checkbox}
              name="relianceCanView"
              label="Udzielam zgody, by pracownicy firmy Reliance mieli dostęp do historii moich transakcji związanych z tym aktywem."
              checked={formik.values.relianceCanView}
              onChange={() =>
                formik.setFieldValue(
                  "relianceCanView",
                  !formik.values.relianceCanView,
                )
              }
            />
          </div>

          <div className="modal-buttons">
            <Button
              type="button"
              className="button--stroke button--rounded"
              onClick={onCancel}
              label="Anuluj"
            />
            <Button
              type="submit"
              className="button--black button--rounded"
              onClick={() => {}}
              label={isPending ? "Zapisywanie..." : "Zapisz"}
              isLoading={isPending}
            />
          </div>
        </Form>
      )}
    </Formik>
  );
};

// ── loans ────────────────────────────────────────────────────────────────────

const INTEREST_PAYMENT_OPTIONS = [
  { value: "monthly", label: "Miesięczne" },
  { value: "quarterly", label: "Kwartalne" },
  { value: "yearly", label: "Roczne" },
  { value: "at_end_of_year", label: "Na koniec roku" },
];

const AUTO_RENEWAL_OPTIONS = [
  { value: "true", label: "Tak" },
  { value: "false", label: "Nie" },
];

const LOAN_TYPE_OPTIONS = [
  { value: "borrowed", label: "Zaciągnięta" },
  { value: "lent", label: "Udzielona" },
];

const loansSchema = Yup.object().shape({
  loanType: Yup.string().required("To pole jest wymagane"),
  entity: Yup.string().required("To pole jest wymagane"),
  primaryValue: Yup.string()
    .required("To pole jest wymagane")
    .test("is-positive", "Wartość musi być dodatnia", (val) => {
      if (!val) return false;
      return !isNaN(Number(val)) && Number(val) > 0;
    }),
  currencyUuid: Yup.string().required("To pole jest wymagane"),
  interestRate: Yup.string()
    .required("To pole jest wymagane")
    .test("is-non-negative", "Wartość nie może być ujemna", (val) => {
      if (!val) return true;
      return !isNaN(Number(val)) && Number(val) >= 0;
    }),
  interestRateType: Yup.string().notRequired(),
  interestPayment: Yup.string().notRequired(),
  contractDate: Yup.string().notRequired(),
  durationDate: Yup.string().notRequired(),
  durationDateValue: Yup.string().when("durationDate", {
    is: "specific_date",
    then: (schema) => schema.required("To pole jest wymagane"),
    otherwise: (schema) => schema.notRequired(),
  }),
  autoRenewal: Yup.string().notRequired(),
  additionalInfo: Yup.string().notRequired(),
  relianceCanView: Yup.boolean(),
  contractFile: Yup.mixed().nullable(),
});

interface LoansValues {
  loanType: string;
  entity: string;
  primaryValue: string;
  currencyUuid: string;
  interestRate: string;
  interestRateType: string;
  interestPayment: string;
  contractDate: string;
  durationDate: string;
  durationDateValue: string;
  autoRenewal: string;
  additionalInfo: string;
  contractFile: File[] | null;
  relianceCanView: boolean;
}

const LoansForm = ({
  item,
  onSubmit,
  onCancel,
  isPending,
  currencyOptions,
  onRemovedContract,
}: {
  item: InvestmentItem;
  onSubmit: (values: LoansValues) => void;
  onCancel: () => void;
  isPending: boolean;
  currencyOptions: any[];
  onRemovedContract: (file: { name: string; url: string }) => void;
}) => {
  const durationDateRaw = item.details?.durationDate as
    | string
    | null
    | undefined;
  let durationDate = "";
  let durationDateValue = "";
  if (durationDateRaw === null || durationDateRaw === undefined) {
    durationDate = "indefinite";
  } else if (typeof durationDateRaw === "string" && durationDateRaw !== "") {
    const d = new Date(durationDateRaw);
    if (
      !isNaN(d.getTime()) &&
      d.getFullYear() === 2000 &&
      d.getMonth() === 0 &&
      d.getDate() === 1
    ) {
      durationDate = "";
    } else if (!isNaN(d.getTime())) {
      durationDate = "specific_date";
      durationDateValue = durationDateRaw;
    }
  }

  const autoRenewalRaw = item.details?.autoRenewal;
  const autoRenewal =
    autoRenewalRaw === true || autoRenewalRaw === 1
      ? "true"
      : autoRenewalRaw === false || autoRenewalRaw === 0
      ? "false"
      : "";

  const existingContractFiles = (item.files ?? []).filter(
    (f: any) => f.role === "contract",
  );

  const initialValues: LoansValues = {
    loanType: String(item.details?.loanType ?? "borrowed"),
    entity: String(item.details?.entity ?? ""),
    primaryValue:
      item.primaryValue !== null && item.primaryValue !== undefined
        ? String(item.primaryValue)
        : "",
    currencyUuid: item.currencyUuid ?? "",
    interestRate: String(item.details?.interestRate ?? ""),
    interestRateType: String(
      item.details?.interestRateType ?? item.details?.interestType ?? "",
    ),
    interestPayment: String(item.details?.interestPayment ?? ""),
    contractDate: item.details?.contractDate
      ? String(item.details.contractDate)
      : "",
    durationDate,
    durationDateValue,
    autoRenewal,
    additionalInfo: item.additionalInfo ?? "",
    contractFile: null,
    relianceCanView: Boolean(item.relianceCanView ?? false),
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={loansSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {(formik) => (
        <Form>
          <div className="modal-form__fields">
            <LargeSwitch
              state={formik.values.loanType}
              options={LOAN_TYPE_OPTIONS}
              onChange={(val: string) => formik.setFieldValue("loanType", val)}
            />

            <Field
              as={Input}
              name="entity"
              label="Podmiot"
              type="text"
              error={
                (formik.touched.entity || formik.submitCount > 0) &&
                formik.errors.entity
                  ? formik.errors.entity
                  : undefined
              }
            />

            <Field
              as={Input}
              name="primaryValue"
              label="Kwota"
              type="number"
              step="any"
              precision={2}
              maxLength={9}
              error={
                (formik.touched.primaryValue || formik.submitCount > 0) &&
                formik.errors.primaryValue
                  ? formik.errors.primaryValue
                  : undefined
              }
            />

            <Field
              as={SelectComponent}
              name="currencyUuid"
              label="Waluta"
              options={currencyOptions}
              isSearchable={true}
              error={
                (formik.touched.currencyUuid || formik.submitCount > 0) &&
                formik.errors.currencyUuid
                  ? formik.errors.currencyUuid
                  : undefined
              }
            />

            <Field
              as={Input}
              name="interestRate"
              label="Wysokość oprocentowania"
              type="number"
              step="any"
              suffix="%"
              precision={2}
              maxLength={9}
              error={
                (formik.touched.interestRate || formik.submitCount > 0) &&
                formik.errors.interestRate
                  ? formik.errors.interestRate
                  : undefined
              }
            />

            <Field
              as={SelectComponent}
              name="interestRateType"
              label="Rodzaj oprocentowania"
              options={INTEREST_TYPE_OPTIONS}
              isSearchable={false}
              optional
            />

            <Field
              as={SelectComponent}
              name="interestPayment"
              label="Odsetki"
              options={INTEREST_PAYMENT_OPTIONS}
              isSearchable={false}
              optional
            />

            <Field
              type="date"
              name="contractDate"
              label="Data zawarcia"
              optional
              component={CalendarInput}
              errors={formik.errors}
            />

            <Field
              as={SelectComponent}
              name="durationDate"
              label="Czas trwania"
              options={DURATION_OPTIONS}
              isSearchable={false}
              optional
            />

            {formik.values.durationDate === "specific_date" && (
              <Field
                type="date"
                name="durationDateValue"
                label="Data zakończenia"
                component={CalendarInput}
                errors={formik.errors}
              />
            )}

            <Field
              as={SelectComponent}
              name="autoRenewal"
              label="Automatyczne przedłużenie"
              options={AUTO_RENEWAL_OPTIONS}
              isSearchable={false}
              optional
            />

            <Field
              component={Textarea}
              name="additionalInfo"
              label="Dodatkowe informacje"
              optional
            />

            <Field
              label="Umowa"
              name="contractFile"
              id="contractFile"
              as={DropFileInput}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              optional
              multiple={10}
              existingFiles={existingContractFiles.map((f: any) => ({
                name: f.file?.name || "Plik",
                url: f.file?.url || "",
              }))}
              onServerFileRemoved={onRemovedContract}
            />

            <Field
              as={Checkbox}
              name="relianceCanView"
              label="Udzielam zgody, by pracownicy firmy Reliance mieli dostęp do historii moich transakcji związanych z tym aktywem."
              checked={formik.values.relianceCanView}
              onChange={() =>
                formik.setFieldValue(
                  "relianceCanView",
                  !formik.values.relianceCanView,
                )
              }
            />
          </div>

          <div className="modal-buttons">
            <Button
              type="button"
              className="button--stroke button--rounded"
              onClick={onCancel}
              label="Anuluj"
            />
            <Button
              type="submit"
              className="button--black button--rounded"
              onClick={() => {}}
              label={isPending ? "Zapisywanie..." : "Zapisz"}
              isLoading={isPending}
            />
          </div>
        </Form>
      )}
    </Formik>
  );
};

// ── real_estate ─────────────────────────────────────────────────────────────

const PROPERTY_TYPE_OPTIONS = [
  { value: "apartment", label: "Mieszkanie" },
  { value: "house", label: "Dom" },
  { value: "land", label: "Działka" },
  { value: "other", label: "Inne" },
];

const realEstateSchema = Yup.object().shape({
  propertyType: Yup.string().required("To pole jest wymagane"),
  propertyTypeOther: Yup.string().when("propertyType", {
    is: "other",
    then: (schema) => schema.required("To pole jest wymagane"),
    otherwise: (schema) => schema.notRequired(),
  }),
  primaryValue: Yup.string()
    .required("To pole jest wymagane")
    .test("is-positive", "Wartość musi być dodatnia", (val) => {
      if (!val) return false;
      return !isNaN(Number(val)) && Number(val) > 0;
    }),
  currencyUuid: Yup.string().required("To pole jest wymagane"),
  area: Yup.string()
    .notRequired()
    .test("is-positive", "Wartość musi być dodatnia", (val) => {
      if (!val) return true;
      return !isNaN(Number(val)) && Number(val) > 0;
    }),
  city: Yup.string().required("To pole jest wymagane"),
  address: Yup.string().required("To pole jest wymagane"),
  additionalInfo: Yup.string().notRequired(),
  contractFile: Yup.mixed().nullable(),
  relianceCanView: Yup.boolean(),
});

interface RealEstateValues {
  propertyType: string;
  propertyTypeOther: string;
  primaryValue: string;
  currencyUuid: string;
  area: string;
  city: string;
  address: string;
  additionalInfo: string;
  photoFiles: File[];
  contractFile: File[] | null;
  relianceCanView: boolean;
}

const RealEstateForm = ({
  item,
  onSubmit,
  onCancel,
  isPending,
  currencyOptions,
  onRemovedPhoto,
  onRemovedContract,
}: {
  item: InvestmentItem;
  onSubmit: (values: RealEstateValues) => void;
  onCancel: () => void;
  isPending: boolean;
  currencyOptions: any[];
  onRemovedPhoto: (file: { name: string; url: string }) => void;
  onRemovedContract: (file: { name: string; url: string }) => void;
}) => {
  const existingContractFiles = (item.files ?? []).filter(
    (f: any) => f.role === "contract",
  );
  const existingPhotos = (item.files ?? []).filter(
    (f: any) => f.role === "photo",
  );

  const initialValues: RealEstateValues = {
    propertyType: String(item.details?.propertyType ?? ""),
    propertyTypeOther: String(item.details?.propertyTypeOther ?? ""),
    primaryValue:
      item.primaryValue !== null && item.primaryValue !== undefined
        ? String(item.primaryValue)
        : "",
    currencyUuid: item.currencyUuid ?? "",
    area:
      item.details?.area !== null && item.details?.area !== undefined
        ? String(item.details.area)
        : "",
    city: String(item.details?.city ?? ""),
    address: String(item.details?.address ?? ""),
    additionalInfo: item.additionalInfo ?? "",
    photoFiles: [],
    contractFile: null,
    relianceCanView: Boolean(item.relianceCanView ?? false),
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={realEstateSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {(formik) => {
        const price = Number(formik.values.primaryValue);
        const area = Number(formik.values.area);
        const pricePerM2 =
          price > 0 && area > 0
            ? (price / area).toLocaleString("pl-PL", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              })
            : "—";

        return (
          <Form>
            <div className="modal-form__fields">
              <RadioGroup
                name="propertyType"
                label=""
                options={PROPERTY_TYPE_OPTIONS}
                value={formik.values.propertyType}
                onChange={(val) => formik.setFieldValue("propertyType", val)}
                error={
                  (formik.touched.propertyType || formik.submitCount > 0) &&
                  formik.errors.propertyType
                    ? formik.errors.propertyType
                    : undefined
                }
              />

              {formik.values.propertyType === "other" && (
                <Field
                  as={Input}
                  name="propertyTypeOther"
                  label="Nazwa"
                  type="text"
                  error={
                    (formik.touched.propertyTypeOther ||
                      formik.submitCount > 0) &&
                    formik.errors.propertyTypeOther
                      ? formik.errors.propertyTypeOther
                      : undefined
                  }
                />
              )}

              <Field
                as={Input}
                name="city"
                label="Miejscowość"
                type="text"
                error={
                  (formik.touched.city || formik.submitCount > 0) &&
                  formik.errors.city
                    ? formik.errors.city
                    : undefined
                }
              />

              <Field
                as={Input}
                name="address"
                label="Adres"
                type="text"
                error={
                  (formik.touched.address || formik.submitCount > 0) &&
                  formik.errors.address
                    ? formik.errors.address
                    : undefined
                }
              />

              <Field
                as={Input}
                name="primaryValue"
                label="Cena zakupu"
                type="number"
                step="any"
                precision={2}
                maxLength={12}
                error={
                  (formik.touched.primaryValue || formik.submitCount > 0) &&
                  formik.errors.primaryValue
                    ? formik.errors.primaryValue
                    : undefined
                }
              />

              <Field
                as={SelectComponent}
                name="currencyUuid"
                label="Waluta"
                options={currencyOptions}
                isSearchable={true}
                error={
                  (formik.touched.currencyUuid || formik.submitCount > 0) &&
                  formik.errors.currencyUuid
                    ? formik.errors.currencyUuid
                    : undefined
                }
              />

              <Field
                as={Input}
                name="area"
                label="Powierzchnia [w m²]"
                type="number"
                step="any"
                precision={2}
                maxLength={10}
                optional
                error={
                  (formik.touched.area || formik.submitCount > 0) &&
                  formik.errors.area
                    ? formik.errors.area
                    : undefined
                }
              />

              <ComputedField label="Cena za m²:" value={pricePerM2} inline />

              <Field
                component={Textarea}
                name="additionalInfo"
                label="Dodatkowe informacje"
                optional
              />

              <Field
                label="Zdjęcia"
                name="photoFiles"
                id="photoFiles"
                as={DropFileInput}
                accept=".png,.jpg,.jpeg"
                multiple={10}
                optional
                showPreview
                existingFiles={
                  existingPhotos.length > 0
                    ? existingPhotos
                        .filter((f: any) => f?.file?.name && f?.file?.url)
                        .map((f: any) => ({
                          name: f.file.name,
                          url: f.file.url,
                        }))
                    : undefined
                }
                onServerFileRemoved={onRemovedPhoto}
              />

              <Field
                label="Umowa"
                name="contractFile"
                id="contractFile"
                as={DropFileInput}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                optional
                multiple={10}
                existingFiles={existingContractFiles.map((f: any) => ({
                  name: f.file?.name || "Plik",
                  url: f.file?.url || "",
                }))}
                onServerFileRemoved={onRemovedContract}
              />

              <Field
                as={Checkbox}
                name="relianceCanView"
                label="Udzielam zgody, by pracownicy firmy Reliance mieli dostęp do historii moich transakcji związanych z tym aktywem."
                checked={formik.values.relianceCanView}
                onChange={() =>
                  formik.setFieldValue(
                    "relianceCanView",
                    !formik.values.relianceCanView,
                  )
                }
              />
            </div>

            <div className="modal-buttons">
              <Button
                type="button"
                className="button--stroke button--rounded"
                onClick={onCancel}
                label="Anuluj"
              />
              <Button
                type="submit"
                className="button--black button--rounded"
                onClick={() => {}}
                label={isPending ? "Zapisywanie..." : "Zapisz"}
                isLoading={isPending}
              />
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

// ── main component ───────────────────────────────────────────────────────────

const otherAssetSchema = Yup.object().shape({
  name: Yup.string().required("Pole wymagane"),
  primaryValue: Yup.number()
    .typeError("Podaj poprawną wartość")
    .required("Pole wymagane")
    .positive("Wartość musi być dodatnia"),
  currencyUuid: Yup.string().required("Pole wymagane"),
});

interface OtherAssetValues {
  name: string;
  primaryValue: string;
  currencyUuid: string;
  additionalInfo: string;
  photoFiles: File[];
  contractFile: File[] | null;
  relianceCanView: boolean;
}

const OtherAssetForm = ({
  item,
  onSubmit,
  onCancel,
  isPending,
  currencyOptions,
  onRemovedPhoto,
  onRemovedContract,
}: {
  item: InvestmentItem;
  onSubmit: (values: OtherAssetValues) => void;
  onCancel: () => void;
  isPending: boolean;
  currencyOptions: any[];
  onRemovedPhoto: (file: { name: string; url: string }) => void;
  onRemovedContract: (file: { name: string; url: string }) => void;
}) => {
  const existingContractFiles = (item.files ?? []).filter(
    (f: any) => f.role === "contract" || f.role === "attachment",
  );
  const existingPhotos = (item.files ?? []).filter(
    (f: any) => f.role === "photo",
  );

  const initialValues: OtherAssetValues = {
    name: item.name ?? "",
    primaryValue:
      item.primaryValue !== null && item.primaryValue !== undefined
        ? String(item.primaryValue)
        : "",
    currencyUuid: item.currencyUuid ?? "",
    additionalInfo: item.additionalInfo ?? "",
    photoFiles: [],
    contractFile: null,
    relianceCanView: Boolean(item.relianceCanView ?? false),
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={otherAssetSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {(formik) => (
        <Form>
          <div className="modal-form__fields">
            <Field
              as={Input}
              name="name"
              label="Nazwa"
              type="text"
              error={
                (formik.touched.name || formik.submitCount > 0) &&
                formik.errors.name
                  ? formik.errors.name
                  : undefined
              }
            />

            <Field
              as={Input}
              name="primaryValue"
              label="Wartość"
              type="number"
              step="any"
              precision={2}
              maxLength={12}
              error={
                (formik.touched.primaryValue || formik.submitCount > 0) &&
                formik.errors.primaryValue
                  ? formik.errors.primaryValue
                  : undefined
              }
            />

            <Field
              as={SelectComponent}
              name="currencyUuid"
              label="Waluta"
              options={currencyOptions}
              isSearchable={true}
              error={
                (formik.touched.currencyUuid || formik.submitCount > 0) &&
                formik.errors.currencyUuid
                  ? formik.errors.currencyUuid
                  : undefined
              }
            />

            <Field
              component={Textarea}
              name="additionalInfo"
              label="Dodatkowe informacje"
              optional
            />

            <Field
              label="Zdjęcia"
              name="photoFiles"
              id="photoFiles"
              as={DropFileInput}
              accept=".png,.jpg,.jpeg"
              multiple={10}
              optional
              showPreview
              existingFiles={
                existingPhotos.length > 0
                  ? existingPhotos
                      .filter((f: any) => f?.file?.name && f?.file?.url)
                      .map((f: any) => ({ name: f.file.name, url: f.file.url }))
                  : undefined
              }
              onServerFileRemoved={onRemovedPhoto}
            />

            <Field
              label="Pliki"
              name="contractFile"
              id="contractFile"
              as={DropFileInput}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              optional
              multiple={10}
              existingFiles={existingContractFiles.map((f: any) => ({
                name: f.file?.name || "Plik",
                url: f.file?.url || "",
              }))}
              onServerFileRemoved={onRemovedContract}
            />

            <Field
              as={Checkbox}
              name="relianceCanView"
              label="Udzielam zgody, by pracownicy firmy Reliance mieli dostęp do historii moich transakcji związanych z tym aktywem."
              checked={formik.values.relianceCanView}
              onChange={() =>
                formik.setFieldValue(
                  "relianceCanView",
                  !formik.values.relianceCanView,
                )
              }
            />
          </div>

          <div className="modal-buttons">
            <Button
              type="button"
              className="button--stroke button--rounded"
              onClick={onCancel}
              label="Anuluj"
            />
            <Button
              type="submit"
              className="button--black button--rounded"
              onClick={() => {}}
              label={isPending ? "Zapisywanie..." : "Zapisz"}
              isLoading={isPending}
            />
          </div>
        </Form>
      )}
    </Formik>
  );
};

const preciousStonesSchema = Yup.object().shape({
  name: Yup.string().required("To pole jest wymagane"),
  primaryValue: Yup.string()
    .required("To pole jest wymagane")
    .test("is-positive", "Wartość musi być dodatnia", (val) => {
      if (!val) return false;
      return !isNaN(Number(val)) && Number(val) > 0;
    }),
  currencyUuid: Yup.string().required("To pole jest wymagane"),
  weight: Yup.string().notRequired(),
  cut: Yup.string().notRequired(),
  color: Yup.string().notRequired(),
  clarity: Yup.string().notRequired(),
  additionalInfo: Yup.string().notRequired(),
  contractFile: Yup.mixed().nullable(),
  relianceCanView: Yup.boolean(),
});

interface PreciousStonesValues {
  name: string;
  primaryValue: string;
  currencyUuid: string;
  weight: string;
  cut: string;
  color: string;
  clarity: string;
  additionalInfo: string;
  contractFile: File[] | null;
  relianceCanView: boolean;
}

const PreciousStonesForm = ({
  item,
  onSubmit,
  onCancel,
  isPending,
  currencyOptions,
  onRemovedContract,
}: {
  item: InvestmentItem;
  onSubmit: (values: PreciousStonesValues) => void;
  onCancel: () => void;
  isPending: boolean;
  currencyOptions: any[];
  onRemovedContract: (file: { name: string; url: string }) => void;
}) => {
  const existingContractFiles = (item.files ?? []).filter(
    (f: any) => f.role === "contract",
  );

  const initialValues: PreciousStonesValues = {
    name: item.name ?? "",
    primaryValue:
      item.primaryValue !== null && item.primaryValue !== undefined
        ? String(item.primaryValue)
        : "",
    currencyUuid: item.currencyUuid ?? "",
    weight: item.details?.weight != null ? String(item.details.weight) : "",
    cut: String(item.details?.cut ?? ""),
    color: String(item.details?.color ?? ""),
    clarity: String(item.details?.clarity ?? ""),
    additionalInfo: item.additionalInfo ?? "",
    contractFile: null,
    relianceCanView: Boolean(item.relianceCanView ?? false),
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={preciousStonesSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {(formik) => (
        <Form>
          <div className="modal-form__fields">
            <Field
              as={Input}
              name="name"
              label="Nazwa"
              type="text"
              error={
                (formik.touched.name || formik.submitCount > 0) &&
                formik.errors.name
                  ? formik.errors.name
                  : undefined
              }
            />

            <Field
              as={Input}
              name="primaryValue"
              label="Cena zakupu"
              type="number"
              precision={2}
              error={
                (formik.touched.primaryValue || formik.submitCount > 0) &&
                formik.errors.primaryValue
                  ? formik.errors.primaryValue
                  : undefined
              }
            />

            <Field
              as={SelectComponent}
              name="currencyUuid"
              label="Waluta"
              options={currencyOptions}
              isSearchable={true}
              error={
                (formik.touched.currencyUuid || formik.submitCount > 0) &&
                formik.errors.currencyUuid
                  ? formik.errors.currencyUuid
                  : undefined
              }
            />

            <Field
              as={Input}
              name="weight"
              label="Masa"
              type="number"
              precision={0}
              suffix="g"
              optional
            />

            <Field as={Input} name="cut" label="Szlif" type="text" optional />

            <Field as={Input} name="color" label="Barwa" type="text" optional />

            <Field
              as={Input}
              name="clarity"
              label="Czystość"
              type="text"
              optional
            />

            <Field
              component={Textarea}
              name="additionalInfo"
              label="Dodatkowe informacje"
              optional
            />

            <Field
              label="Umowa"
              name="contractFile"
              id="contractFile"
              as={DropFileInput}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              optional
              multiple={10}
              existingFiles={existingContractFiles.map((f: any) => ({
                name: f.file?.name || "Plik",
                url: f.file?.url || "",
              }))}
              onServerFileRemoved={onRemovedContract}
            />

            <Field
              as={Checkbox}
              name="relianceCanView"
              label="Udzielam zgody, by pracownicy firmy Reliance mieli dostęp do historii moich transakcji związanych z tym aktywem."
              checked={formik.values.relianceCanView}
              onChange={() =>
                formik.setFieldValue(
                  "relianceCanView",
                  !formik.values.relianceCanView,
                )
              }
            />
          </div>

          <div className="modal-buttons">
            <Button
              type="button"
              className="button--stroke button--rounded"
              onClick={onCancel}
              label="Anuluj"
            />
            <Button
              type="submit"
              className="button--black button--rounded"
              onClick={() => {}}
              label={isPending ? "Zapisywanie..." : "Zapisz"}
              isLoading={isPending}
            />
          </div>
        </Form>
      )}
    </Formik>
  );
};

const artworkSchema = Yup.object().shape({
  artworkType: Yup.string().required("To pole jest wymagane"),
  primaryValue: Yup.string()
    .required("To pole jest wymagane")
    .test("is-positive", "Wartość musi być dodatnia", (val) => {
      if (!val) return false;
      return !isNaN(Number(val)) && Number(val) > 0;
    }),
  currencyUuid: Yup.string().required("To pole jest wymagane"),
  name: Yup.string().notRequired(),
  unknownAuthor: Yup.boolean(),
  author: Yup.string().when("unknownAuthor", {
    is: true,
    then: (schema) => schema.notRequired(),
    otherwise: (schema) => schema.required("To pole jest wymagane"),
  }),
  eraAge: Yup.string().notRequired(),
  additionalInfo: Yup.string().notRequired(),
  contractFile: Yup.mixed().nullable(),
  relianceCanView: Yup.boolean(),
});

interface ArtworkValues {
  artworkType: string;
  primaryValue: string;
  currencyUuid: string;
  name: string;
  unknownAuthor: boolean;
  author: string;
  eraAge: string;
  additionalInfo: string;
  photoFiles: File[];
  contractFile: File[] | null;
  relianceCanView: boolean;
}

const ArtworkForm = ({
  item,
  onSubmit,
  onCancel,
  isPending,
  currencyOptions,
  onRemovedPhoto,
  onRemovedContract,
}: {
  item: InvestmentItem;
  onSubmit: (values: ArtworkValues) => void;
  onCancel: () => void;
  isPending: boolean;
  currencyOptions: any[];
  onRemovedPhoto: (file: { name: string; url: string }) => void;
  onRemovedContract: (file: { name: string; url: string }) => void;
}) => {
  const existingContractFiles = (item.files ?? []).filter(
    (f: any) => f.role === "contract",
  );
  const existingPhotos = (item.files ?? []).filter(
    (f: any) => f.role === "photo",
  );

  const initialValues: ArtworkValues = {
    artworkType: String(item.details?.artworkType ?? ""),
    primaryValue:
      item.primaryValue !== null && item.primaryValue !== undefined
        ? String(item.primaryValue)
        : "",
    currencyUuid: item.currencyUuid ?? "",
    name: item.name ?? "",
    unknownAuthor: item.details?.author === "Autor nieznany",
    author: String(item.details?.author ?? ""),
    eraAge: String(item.details?.eraAge ?? ""),
    additionalInfo: item.additionalInfo ?? "",
    photoFiles: [],
    contractFile: null,
    relianceCanView: Boolean(item.relianceCanView ?? false),
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={artworkSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {(formik) => (
        <Form>
          <div className="modal-form__fields">
            <Field
              as={Input}
              name="artworkType"
              label="Rodzaj"
              type="text"
              error={
                (formik.touched.artworkType || formik.submitCount > 0) &&
                formik.errors.artworkType
                  ? formik.errors.artworkType
                  : undefined
              }
            />

            <Field
              as={Input}
              name="primaryValue"
              label="Cena zakupu"
              type="number"
              precision={2}
              error={
                (formik.touched.primaryValue || formik.submitCount > 0) &&
                formik.errors.primaryValue
                  ? formik.errors.primaryValue
                  : undefined
              }
            />

            <Field
              as={SelectComponent}
              name="currencyUuid"
              label="Waluta"
              options={currencyOptions}
              isSearchable={true}
              error={
                (formik.touched.currencyUuid || formik.submitCount > 0) &&
                formik.errors.currencyUuid
                  ? formik.errors.currencyUuid
                  : undefined
              }
            />

            <Field as={Input} name="name" label="Tytuł" type="text" optional />

            <Field
              as={Checkbox}
              name="unknownAuthor"
              label="Autor nieznany"
              checked={formik.values.unknownAuthor}
              onChange={() => {
                const newVal = !formik.values.unknownAuthor;
                formik.setFieldValue("unknownAuthor", newVal);
                formik.setFieldValue("author", newVal ? "Autor nieznany" : "");
              }}
            />

            <Field
              as={Input}
              name="author"
              label="Autor"
              type="text"
              disabled={formik.values.unknownAuthor}
              optional={formik.values.unknownAuthor}
              error={
                !formik.values.unknownAuthor &&
                (formik.touched.author || formik.submitCount > 0) &&
                formik.errors.author
                  ? formik.errors.author
                  : undefined
              }
            />

            <Field
              as={Input}
              name="eraAge"
              label="Epoka/Wiek"
              type="text"
              optional
            />

            <Field
              component={Textarea}
              name="additionalInfo"
              label="Dodatkowe informacje"
              optional
            />

            <Field
              label="Zdjęcia"
              name="photoFiles"
              id="photoFiles"
              as={DropFileInput}
              accept=".png,.jpg,.jpeg"
              multiple={10}
              optional
              showPreview
              existingFiles={
                existingPhotos.length > 0
                  ? existingPhotos
                      .filter((f: any) => f?.file?.name && f?.file?.url)
                      .map((f: any) => ({ name: f.file.name, url: f.file.url }))
                  : undefined
              }
              onServerFileRemoved={onRemovedPhoto}
            />

            <Field
              label="Umowa"
              name="contractFile"
              id="contractFile"
              as={DropFileInput}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              optional
              multiple={10}
              existingFiles={existingContractFiles.map((f: any) => ({
                name: f.file?.name || "Plik",
                url: f.file?.url || "",
              }))}
              onServerFileRemoved={onRemovedContract}
            />

            <Field
              as={Checkbox}
              name="relianceCanView"
              label="Udzielam zgody, by pracownicy firmy Reliance mieli dostęp do historii moich transakcji związanych z tym aktywem."
              checked={formik.values.relianceCanView}
              onChange={() =>
                formik.setFieldValue(
                  "relianceCanView",
                  !formik.values.relianceCanView,
                )
              }
            />
          </div>

          <div className="modal-buttons">
            <Button
              type="button"
              className="button--stroke button--rounded"
              onClick={onCancel}
              label="Anuluj"
            />
            <Button
              type="submit"
              className="button--black button--rounded"
              onClick={() => {}}
              label={isPending ? "Zapisywanie..." : "Zapisz"}
              isLoading={isPending}
            />
          </div>
        </Form>
      )}
    </Formik>
  );
};

// ── main component ───────────────────────────────────────────────────────────

const EditInvestmentModal = ({
  isOpen,
  onClose,
  item,
  slug,
}: EditInvestmentModalProps) => {
  const { mutate: updateInvestment, isPending } = useUpdateInvestment({
    onError: (error) => {
      if ((error as any).status === 404) {
        onClose();
      }
    },
  });
  const [isUploading, setIsUploading] = useState(false);
  const { data: currenciesData } = useGetCountriesCurrency();
  const currencyOptions = currenciesData ?? [];
  const { mutateAsync: uploadFile } = useUploadInvestmentFile();
  const { mutateAsync: attachFile } = useAttachInvestmentFile();
  const { mutateAsync: deleteFile } = useDeleteInvestmentFile();
  const { data: me } = useGetMe();
  const artworkRemovedPhotos = useRef<Set<string>>(new Set());
  const realEstateRemovedPhotos = useRef<Set<string>>(new Set());
  const otherAssetRemovedPhotos = useRef<Set<string>>(new Set());

  const artworkRemovedContracts = useRef<Set<string>>(new Set());
  const realEstateRemovedContracts = useRef<Set<string>>(new Set());
  const otherAssetRemovedContracts = useRef<Set<string>>(new Set());
  const loansRemovedContracts = useRef<Set<string>>(new Set());
  const investmentPolicyRemovedContracts = useRef<Set<string>>(new Set());
  const treasuryRemovedContracts = useRef<Set<string>>(new Set());
  const preciousStonesRemovedContracts = useRef<Set<string>>(new Set());

  useEffect(() => {
    artworkRemovedPhotos.current = new Set();
    realEstateRemovedPhotos.current = new Set();
    otherAssetRemovedPhotos.current = new Set();

    artworkRemovedContracts.current = new Set();
    realEstateRemovedContracts.current = new Set();
    otherAssetRemovedContracts.current = new Set();
    loansRemovedContracts.current = new Set();
    investmentPolicyRemovedContracts.current = new Set();
    treasuryRemovedContracts.current = new Set();
    preciousStonesRemovedContracts.current = new Set();
  }, [item?.uuid]);

  const handleLoansSubmit = async (values: LoansValues) => {
    if (!item) return;

    const autoRenewalVal =
      values.autoRenewal === "true"
        ? true
        : values.autoRenewal === "false"
        ? false
        : null;

    let durationDateVal: string | null = "2000-01-01";
    if (values.durationDate === "indefinite") {
      durationDateVal = null;
    } else if (values.durationDate === "specific_date") {
      durationDateVal = values.durationDateValue || "";
    }

    const existingContractFiles = (item.files ?? []).filter(
      (f: any) => f.role === "contract",
    );
    const removedContractUrls = loansRemovedContracts.current;
    const contractsToDelete = existingContractFiles.filter(
      (c: any) => c?.file?.url && removedContractUrls.has(c.file.url),
    );
    const newLocalContracts = Array.isArray(values.contractFile)
      ? (values.contractFile as any[]).filter((f) => f instanceof File)
      : [];
    const hasContractChanges =
      contractsToDelete.length > 0 || newLocalContracts.length > 0;

    updateInvestment(
      {
        uuid: item.uuid,
        primaryValue:
          values.primaryValue !== "" ? Number(values.primaryValue) : undefined,
        currencyUuid: values.currencyUuid || undefined,
        additionalInfo: values.additionalInfo || null,
        relianceCanView: Boolean(values.relianceCanView),
        details: {
          loanType: values.loanType,
          entity: values.entity,
          interestRate:
            values.interestRate !== "" ? Number(values.interestRate) : null,
          interestRateType: values.interestRateType || null,
          interestPayment: values.interestPayment || null,
          contractDate: values.contractDate || null,
          durationDate: durationDateVal,
          autoRenewal: autoRenewalVal,
        },
      } as any,
      {
        onSuccess: async () => {
          setIsUploading(true);
          if (hasContractChanges) {
            try {
              const userId: string | number =
                (me as any)?.user?.id ?? (me as any)?.id ?? "";

              for (const contractFile of contractsToDelete) {
                if (contractFile?.file?.uuid) {
                  await deleteFile({
                    investmentUuid: item.uuid,
                    fileUuid: contractFile.file.uuid,
                  });
                }
              }

              await Promise.allSettled(
                newLocalContracts.slice(0, 10).map(async (contract: File) => {
                  try {
                    const uploaded = await uploadFile({
                      file: contract,
                      type: "attachment",
                      parentId: userId,
                    });
                    const fileUuid = Array.isArray(uploaded)
                      ? uploaded[0]?.uuid
                      : (uploaded as any)?.uuid;
                    if (fileUuid) {
                      await attachFile({
                        investmentUuid: item.uuid,
                        fileUuid,
                        role: "contract",
                      });
                    }
                  } catch (error) {
                    console.error("Error uploading contract:", error);
                  }
                }),
              );
            } catch (error) {
              console.error("Error handling contract files:", error);
            }
          }
          setIsUploading(false);
          onClose();
        },
      },
    );
  };

  const handleRealEstateSubmit = async (values: RealEstateValues) => {
    if (!item) return;

    const existingPhotoFiles = (item.files ?? []).filter(
      (f: any) => f.role === "photo",
    );
    const existingContractFiles = (item.files ?? []).filter(
      (f: any) => f.role === "contract",
    );
    const removedContractUrls = realEstateRemovedContracts.current;
    const contractsToDelete = existingContractFiles.filter(
      (c: any) => c?.file?.url && removedContractUrls.has(c.file.url),
    );
    const newLocalContracts = Array.isArray(values.contractFile)
      ? (values.contractFile as any[]).filter((f) => f instanceof File)
      : [];
    const hasContractChanges =
      contractsToDelete.length > 0 || newLocalContracts.length > 0;
    const removedPhotoUrls = realEstateRemovedPhotos.current;
    const photosToDelete = existingPhotoFiles.filter(
      (p: any) => p?.file?.url && removedPhotoUrls.has(p.file.url),
    );
    const newLocalPhotos = Array.isArray(values.photoFiles)
      ? (values.photoFiles as any[]).filter((f) => f instanceof File)
      : [];
    const hasPhotoChanges =
      photosToDelete.length > 0 || newLocalPhotos.length > 0;

    updateInvestment(
      {
        uuid: item.uuid,
        primaryValue:
          values.primaryValue !== "" ? Number(values.primaryValue) : undefined,
        currencyUuid: values.currencyUuid || undefined,
        additionalInfo: values.additionalInfo || null,
        relianceCanView: Boolean(values.relianceCanView),
        details: {
          propertyType: values.propertyType,
          propertyTypeOther:
            values.propertyType === "other"
              ? values.propertyTypeOther || null
              : null,
          area: values.area !== "" ? Number(values.area) : null,
          city: values.city || null,
          address: values.address || null,
        },
      } as any,
      {
        onSuccess: async () => {
          setIsUploading(true);
          const userId: string | number =
            (me as any)?.user?.id ?? (me as any)?.id ?? "";

          if (hasContractChanges) {
            try {
              for (const contractFile of contractsToDelete) {
                if (contractFile?.file?.uuid) {
                  await deleteFile({
                    investmentUuid: item.uuid,
                    fileUuid: contractFile.file.uuid,
                  });
                }
              }
              await Promise.allSettled(
                newLocalContracts.slice(0, 10).map(async (contract: File) => {
                  try {
                    const uploaded = await uploadFile({
                      file: contract,
                      type: "attachment",
                      parentId: userId,
                    });
                    const fileUuid = Array.isArray(uploaded)
                      ? uploaded[0]?.uuid
                      : (uploaded as any)?.uuid;
                    if (fileUuid) {
                      await attachFile({
                        investmentUuid: item.uuid,
                        fileUuid,
                        role: "contract",
                      });
                    }
                  } catch (error) {
                    console.error("Error uploading contract:", error);
                  }
                }),
              );
            } catch (error) {
              console.error("Error handling contract files:", error);
            }
          }

          if (hasPhotoChanges) {
            try {
              for (const photoFile of photosToDelete) {
                if (photoFile?.file?.uuid) {
                  await deleteFile({
                    investmentUuid: item.uuid,
                    fileUuid: photoFile.file.uuid,
                  });
                }
              }
              await Promise.allSettled(
                newLocalPhotos.slice(0, 10).map(async (photo: File) => {
                  try {
                    const uploaded = await uploadFile({
                      file: photo,
                      type: "image",
                      parentId: userId,
                    });
                    const fileUuid = Array.isArray(uploaded)
                      ? uploaded[0]?.uuid
                      : (uploaded as any)?.uuid;
                    if (fileUuid) {
                      await attachFile({
                        investmentUuid: item.uuid,
                        fileUuid,
                        role: "photo",
                      });
                    }
                  } catch {
                    // pojedyncze zdjęcie nie blokuje
                  }
                }),
              );
            } catch (error) {
              console.error("Error handling photo files:", error);
            }
          }

          setIsUploading(false);
          onClose();
        },
      },
    );
  };

  const handleTreasurySubmit = async (values: TreasuryBondsValues) => {
    if (!item) return;

    const existingContractFiles = (item.files ?? []).filter(
      (f: any) => f.role === "contract",
    );
    const newLocalContracts = Array.isArray(values.contractFile)
      ? (values.contractFile as any[]).filter((f) => f instanceof File)
      : [];
    const removedContractUrls = treasuryRemovedContracts.current;
    const contractsToDelete = existingContractFiles.filter(
      (c: any) => c?.file?.url && removedContractUrls.has(c.file.url),
    );
    const hasContractChanges =
      contractsToDelete.length > 0 || newLocalContracts.length > 0;

    updateInvestment(
      {
        uuid: item.uuid,
        quantity: values.quantity !== "" ? Number(values.quantity) : undefined,
        currencyUuid: values.currencyUuid || undefined,
        primaryValue:
          values.primaryValue !== "" ? Number(values.primaryValue) : undefined,
        additionalInfo: values.additionalInfo || null,
        relianceCanView: Boolean(values.relianceCanView),
        details: {
          interestRate:
            values.interestRate !== "" ? Number(values.interestRate) : null,
          interestRateType: values.interestType || null,
        },
      } as any,
      {
        onSuccess: async () => {
          setIsUploading(true);
          if (hasContractChanges) {
            try {
              const userId: string | number =
                (me as any)?.user?.id ?? (me as any)?.id ?? "";

              for (const contractFile of contractsToDelete) {
                if (contractFile?.file?.uuid) {
                  await deleteFile({
                    investmentUuid: item.uuid,
                    fileUuid: contractFile.file.uuid,
                  });
                }
              }

              await Promise.allSettled(
                newLocalContracts.slice(0, 10).map(async (contract: File) => {
                  try {
                    const uploaded = await uploadFile({
                      file: contract,
                      type: "attachment",
                      parentId: userId,
                    });
                    const fileUuid = Array.isArray(uploaded)
                      ? uploaded[0]?.uuid
                      : (uploaded as any)?.uuid;
                    if (fileUuid) {
                      await attachFile({
                        investmentUuid: item.uuid,
                        fileUuid,
                        role: "contract",
                      });
                    }
                  } catch (error) {
                    console.error("Error uploading contract:", error);
                  }
                }),
              );
            } catch (error) {
              console.error("Error handling contract files:", error);
            }
          }

          setIsUploading(false);
          onClose();
        },
      },
    );
  };

  const handleCryptoSubmit = (values: CryptoValues) => {
    if (!item) return;
    updateInvestment(
      {
        uuid: item.uuid,
        name: values.name,
        additionalInfo: values.additionalInfo || null,
        relianceCanView: Boolean(values.relianceCanView),
      },
      { onSuccess: () => onClose() },
    );
  };

  const handleCurrenciesSubmit = (values: CurrenciesValues) => {
    if (!item) return;
    updateInvestment(
      {
        uuid: item.uuid,
        additionalInfo: values.additionalInfo || null,
        relianceCanView: Boolean(values.relianceCanView),
      },
      { onSuccess: () => onClose() },
    );
  };

  const handleInvestmentPolicySubmit = async (
    values: InvestmentPolicyValues,
  ) => {
    if (!item) return;

    // Transformacja durationDate zgodnie z logiką z fieldConfigs.ts
    let durationDate: string | null = "2000-01-01";
    if (values.durationDate === "indefinite") {
      durationDate = null;
    } else if (values.durationDate === "specific_date") {
      durationDate = values.durationDateValue || "";
    }

    const existingContractFiles = (item.files ?? []).filter(
      (f: any) => f.role === "contract",
    );
    const removedContractUrls = investmentPolicyRemovedContracts.current;
    const contractsToDelete = existingContractFiles.filter(
      (c: any) => c?.file?.url && removedContractUrls.has(c.file.url),
    );
    const newLocalContracts = Array.isArray(values.contractFile)
      ? (values.contractFile as any[]).filter((f) => f instanceof File)
      : [];
    const hasContractChanges =
      contractsToDelete.length > 0 || newLocalContracts.length > 0;

    updateInvestment(
      {
        uuid: item.uuid,
        name: values.name,
        additionalInfo: values.additionalInfo || null,
        relianceCanView: Boolean(values.relianceCanView),
        details: {
          currencyUuid: values.currencyUuid,
          contractDate: values.contractDate || null,
          durationDate,
          premiumPayment: values.premiumPayment || null,
        },
      },
      {
        onSuccess: async () => {
          setIsUploading(true);
          if (hasContractChanges) {
            try {
              const userId: string | number =
                (me as any)?.user?.id ?? (me as any)?.id ?? "";

              // Usuń tylko pliki contract usunięte przez użytkownika
              for (const contractFile of contractsToDelete) {
                if (contractFile?.file?.uuid) {
                  await deleteFile({
                    investmentUuid: item.uuid,
                    fileUuid: contractFile.file.uuid,
                  });
                }
              }

              // Upload wszystkich nowych plików
              await Promise.allSettled(
                newLocalContracts.slice(0, 10).map(async (contract: File) => {
                  try {
                    const uploaded = await uploadFile({
                      file: contract,
                      type: "attachment",
                      parentId: userId,
                    });
                    const fileUuid = Array.isArray(uploaded)
                      ? uploaded[0]?.uuid
                      : (uploaded as any)?.uuid;
                    if (fileUuid) {
                      await attachFile({
                        investmentUuid: item.uuid,
                        fileUuid,
                        role: "contract",
                      });
                    }
                  } catch (error) {
                    console.error("Error uploading contract:", error);
                  }
                }),
              );
            } catch (error) {
              console.error("Error handling contract files:", error);
            }
          }
          setIsUploading(false);
          onClose();
        },
      },
    );
  };

  const handleOtherAssetSubmit = async (values: OtherAssetValues) => {
    if (!item) return;

    const existingContractFiles = (item.files ?? []).filter(
      (f: any) => f.role === "contract" || f.role === "attachment",
    );
    const existingPhotoFiles = (item.files ?? []).filter(
      (f: any) => f.role === "photo",
    );
    const newLocalContracts = Array.isArray(values.contractFile)
      ? (values.contractFile as any[]).filter((f) => f instanceof File)
      : [];
    const removedContractUrls = otherAssetRemovedContracts.current;
    const contractsToDelete = existingContractFiles.filter(
      (c: any) => c?.file?.url && removedContractUrls.has(c.file.url),
    );
    const hasContractChanges =
      contractsToDelete.length > 0 || newLocalContracts.length > 0;
    const removedPhotoUrls = otherAssetRemovedPhotos.current;
    const photosToDelete = existingPhotoFiles.filter(
      (p: any) => p?.file?.url && removedPhotoUrls.has(p.file.url),
    );
    const newLocalPhotos = Array.isArray(values.photoFiles)
      ? (values.photoFiles as any[]).filter((f) => f instanceof File)
      : [];
    const hasPhotoChanges =
      photosToDelete.length > 0 || newLocalPhotos.length > 0;

    updateInvestment(
      {
        uuid: item.uuid,
        name: values.name || null,
        primaryValue:
          values.primaryValue !== "" ? Number(values.primaryValue) : undefined,
        currencyUuid: values.currencyUuid || undefined,
        additionalInfo: values.additionalInfo || null,
        relianceCanView: Boolean(values.relianceCanView),
      } as any,
      {
        onSuccess: async () => {
          setIsUploading(true);
          const userId: string | number =
            (me as any)?.user?.id ?? (me as any)?.id ?? "";

          if (hasContractChanges) {
            try {
              // Usuń tylko pliki contract usunięte przez użytkownika
              for (const contractFile of contractsToDelete) {
                if (contractFile?.file?.uuid) {
                  await deleteFile({
                    investmentUuid: item.uuid,
                    fileUuid: contractFile.file.uuid,
                  });
                }
              }
              // Upload wszystkich nowych plików
              await Promise.allSettled(
                newLocalContracts.slice(0, 10).map(async (contract: File) => {
                  try {
                    const uploaded = await uploadFile({
                      file: contract,
                      type: "attachment",
                      parentId: userId,
                    });
                    const fileUuid = Array.isArray(uploaded)
                      ? uploaded[0]?.uuid
                      : (uploaded as any)?.uuid;
                    if (fileUuid) {
                      await attachFile({
                        investmentUuid: item.uuid,
                        fileUuid,
                        role: "contract",
                      });
                    }
                  } catch (error) {
                    console.error("Error uploading contract:", error);
                  }
                }),
              );
            } catch (error) {
              console.error("Error handling contract files:", error);
            }
          }

          if (hasPhotoChanges) {
            try {
              for (const photoFile of photosToDelete) {
                if (photoFile?.file?.uuid) {
                  await deleteFile({
                    investmentUuid: item.uuid,
                    fileUuid: photoFile.file.uuid,
                  });
                }
              }
              await Promise.allSettled(
                newLocalPhotos.slice(0, 10).map(async (photo: File) => {
                  try {
                    const uploaded = await uploadFile({
                      file: photo,
                      type: "image",
                      parentId: userId,
                    });
                    const fileUuid = Array.isArray(uploaded)
                      ? uploaded[0]?.uuid
                      : (uploaded as any)?.uuid;
                    if (fileUuid) {
                      await attachFile({
                        investmentUuid: item.uuid,
                        fileUuid,
                        role: "photo",
                      });
                    }
                  } catch {
                    // pojedyncze zdjęcie nie blokuje
                  }
                }),
              );
            } catch (error) {
              console.error("Error handling photo files:", error);
            }
          }

          setIsUploading(false);
          onClose();
        },
      },
    );
  };

  const handleArtworkSubmit = async (values: ArtworkValues) => {
    if (!item) return;

    const existingContractFiles = (item.files ?? []).filter(
      (f: any) => f.role === "contract",
    );
    const existingPhotoFiles = (item.files ?? []).filter(
      (f: any) => f.role === "photo",
    );
    const newLocalContracts = Array.isArray(values.contractFile)
      ? (values.contractFile as any[]).filter((f) => f instanceof File)
      : [];
    const removedContractUrls = artworkRemovedContracts.current;
    const contractsToDelete = existingContractFiles.filter(
      (c: any) => c?.file?.url && removedContractUrls.has(c.file.url),
    );
    const hasContractChanges =
      contractsToDelete.length > 0 || newLocalContracts.length > 0;
    const removedPhotoUrls = artworkRemovedPhotos.current;
    const photosToDelete = existingPhotoFiles.filter(
      (p: any) => p?.file?.url && removedPhotoUrls.has(p.file.url),
    );
    const newLocalPhotos = Array.isArray(values.photoFiles)
      ? (values.photoFiles as any[]).filter((f) => f instanceof File)
      : [];
    const hasPhotoChanges =
      photosToDelete.length > 0 || newLocalPhotos.length > 0;

    updateInvestment(
      {
        uuid: item.uuid,
        name: values.name || null,
        primaryValue:
          values.primaryValue !== "" ? Number(values.primaryValue) : undefined,
        currencyUuid: values.currencyUuid || undefined,
        additionalInfo: values.additionalInfo || null,
        relianceCanView: Boolean(values.relianceCanView),
        details: {
          artworkType: values.artworkType,
          author: values.author || null,
          eraAge: values.eraAge || null,
        },
      } as any,
      {
        onSuccess: async () => {
          setIsUploading(true);
          const userId: string | number =
            (me as any)?.user?.id ?? (me as any)?.id ?? "";

          // Obsługa nowego pliku umowy
          if (hasContractChanges) {
            try {
              // Usuń tylko pliki contract usunięte przez użytkownika
              for (const contractFile of contractsToDelete) {
                if (contractFile?.file?.uuid) {
                  await deleteFile({
                    investmentUuid: item.uuid,
                    fileUuid: contractFile.file.uuid,
                  });
                }
              }
              // Upload wszystkich nowych plików
              await Promise.allSettled(
                newLocalContracts.slice(0, 10).map(async (contract: File) => {
                  try {
                    const uploaded = await uploadFile({
                      file: contract,
                      type: "attachment",
                      parentId: userId,
                    });
                    const fileUuid = Array.isArray(uploaded)
                      ? uploaded[0]?.uuid
                      : (uploaded as any)?.uuid;
                    if (fileUuid) {
                      await attachFile({
                        investmentUuid: item.uuid,
                        fileUuid,
                        role: "contract",
                      });
                    }
                  } catch (error) {
                    console.error("Error uploading contract:", error);
                  }
                }),
              );
            } catch (error) {
              console.error("Error handling contract files:", error);
            }
          }

          // Obsługa zdjęć: usuń tylko te usunięte przez usera, dodaj tylko nowe
          if (hasPhotoChanges) {
            try {
              // Usuń tylko te, które user jawnie usunął z listy
              for (const photoFile of photosToDelete) {
                if (photoFile?.file?.uuid) {
                  await deleteFile({
                    investmentUuid: item.uuid,
                    fileUuid: photoFile.file.uuid,
                  });
                }
              }
              // Wgraj i podepnij tylko nowe lokalne pliki
              await Promise.allSettled(
                newLocalPhotos.slice(0, 10).map(async (photo: File) => {
                  try {
                    const uploaded = await uploadFile({
                      file: photo,
                      type: "image",
                      parentId: userId,
                    });
                    const fileUuid = Array.isArray(uploaded)
                      ? uploaded[0]?.uuid
                      : (uploaded as any)?.uuid;
                    if (fileUuid) {
                      await attachFile({
                        investmentUuid: item.uuid,
                        fileUuid,
                        role: "photo",
                      });
                    }
                  } catch {
                    // pojedyncze zdjęcie nie blokuje
                  }
                }),
              );
            } catch (error) {
              console.error("Error handling photo files:", error);
            }
          }

          setIsUploading(false);
          onClose();
        },
      },
    );
  };

  const handlePreciousStonesSubmit = async (values: PreciousStonesValues) => {
    if (!item) return;

    const existingContractFiles = (item.files ?? []).filter(
      (f: any) => f.role === "contract",
    );
    const newLocalContracts = Array.isArray(values.contractFile)
      ? (values.contractFile as any[]).filter((f) => f instanceof File)
      : [];
    const removedContractUrls = preciousStonesRemovedContracts.current;
    const contractsToDelete = existingContractFiles.filter(
      (c: any) => c?.file?.url && removedContractUrls.has(c.file.url),
    );
    const hasContractChanges =
      contractsToDelete.length > 0 || newLocalContracts.length > 0;

    updateInvestment(
      {
        uuid: item.uuid,
        name: values.name || null,
        primaryValue:
          values.primaryValue !== "" ? Number(values.primaryValue) : undefined,
        currencyUuid: values.currencyUuid || undefined,
        additionalInfo: values.additionalInfo || null,
        relianceCanView: Boolean(values.relianceCanView),
        details: {
          weight: values.weight !== "" ? Number(values.weight) : null,
          cut: values.cut || null,
          color: values.color || null,
          clarity: values.clarity || null,
        },
      } as any,
      {
        onSuccess: async () => {
          setIsUploading(true);
          const userId: string | number =
            (me as any)?.user?.id ?? (me as any)?.id ?? "";

          if (hasContractChanges) {
            try {
              for (const contractFile of contractsToDelete) {
                if (contractFile?.file?.uuid) {
                  await deleteFile({
                    investmentUuid: item.uuid,
                    fileUuid: contractFile.file.uuid,
                  });
                }
              }
              await Promise.allSettled(
                newLocalContracts.slice(0, 10).map(async (contract: File) => {
                  try {
                    const uploaded = await uploadFile({
                      file: contract,
                      type: "attachment",
                      parentId: userId,
                    });
                    const fileUuid = Array.isArray(uploaded)
                      ? uploaded[0]?.uuid
                      : (uploaded as any)?.uuid;
                    if (fileUuid) {
                      await attachFile({
                        investmentUuid: item.uuid,
                        fileUuid,
                        role: "contract",
                      });
                    }
                  } catch (error) {
                    console.error("Error uploading contract:", error);
                  }
                }),
              );
            } catch (error) {
              console.error("Error handling contract files:", error);
            }
          }

          setIsUploading(false);
          onClose();
        },
      },
    );
  };

  const getTitle = () => {
    switch (slug) {
      case "treasury_bonds":
        return "Edycja obligacji skarbowych zakupionych poza Reliance";
      case "currencies":
        return "Edycja walut zakupionych poza Reliance";
      case "crypto":
        return "Edycja tokenów / kryptowalut zakupionych poza Reliance";
      case "investment_policy":
        return "Edycja polisy inwestycyjnej / IKE zakupionej poza Reliance";
      case "artwork":
        return "Edycja dzieła sztuki zakupionego poza Reliance";
      case "precious_stones":
        return "Edycja kamienia szlachetnego";
      case "real_estate":
        return "Edycja nieruchomości";
      case "loans":
        return "Edycja pożyczki";
      default:
        return "Edycja aktywa";
    }
  };

  return (
    <FullscreenModal isOpen={isOpen} onClose={onClose}>
      <>
        <h2 className="fullscreen-modal__header">{getTitle()}</h2>

        {item && slug === "treasury_bonds" && (
          <TreasuryBondsForm
            item={item}
            onSubmit={handleTreasurySubmit}
            onCancel={onClose}
            isPending={isPending || isUploading}
            currencyOptions={currencyOptions}
            onRemovedContract={(f) =>
              treasuryRemovedContracts.current.add(f.url)
            }
          />
        )}
        {item && slug === "crypto" && (
          <CryptoForm
            item={item}
            onSubmit={handleCryptoSubmit}
            onCancel={onClose}
            isPending={isPending || isUploading}
          />
        )}
        {item && slug === "currencies" && (
          <CurrenciesForm
            item={item}
            onSubmit={handleCurrenciesSubmit}
            onCancel={onClose}
            isPending={isPending || isUploading}
          />
        )}
        {item && slug === "investment_policy" && (
          <InvestmentPolicyForm
            item={item}
            onSubmit={handleInvestmentPolicySubmit}
            onCancel={onClose}
            isPending={isPending || isUploading}
            currencyOptions={currencyOptions}
            onRemovedContract={(f) =>
              investmentPolicyRemovedContracts.current.add(f.url)
            }
          />
        )}
        {item && slug === "artwork" && (
          <ArtworkForm
            item={item}
            onSubmit={handleArtworkSubmit}
            onCancel={onClose}
            isPending={isPending || isUploading}
            currencyOptions={currencyOptions}
            onRemovedPhoto={(f) => artworkRemovedPhotos.current.add(f.url)}
            onRemovedContract={(f) =>
              artworkRemovedContracts.current.add(f.url)
            }
          />
        )}
        {item && slug === "precious_stones" && (
          <PreciousStonesForm
            item={item}
            onSubmit={handlePreciousStonesSubmit}
            onCancel={onClose}
            isPending={isPending || isUploading}
            currencyOptions={currencyOptions}
            onRemovedContract={(f) =>
              preciousStonesRemovedContracts.current.add(f.url)
            }
          />
        )}
        {item && slug === "loans" && (
          <LoansForm
            item={item}
            onSubmit={handleLoansSubmit}
            onCancel={onClose}
            isPending={isPending || isUploading}
            currencyOptions={currencyOptions}
            onRemovedContract={(f) => loansRemovedContracts.current.add(f.url)}
          />
        )}
        {item && slug === "real_estate" && (
          <RealEstateForm
            item={item}
            onSubmit={handleRealEstateSubmit}
            onCancel={onClose}
            isPending={isPending || isUploading}
            currencyOptions={currencyOptions}
            onRemovedPhoto={(f) => realEstateRemovedPhotos.current.add(f.url)}
            onRemovedContract={(f) =>
              realEstateRemovedContracts.current.add(f.url)
            }
          />
        )}
        {item &&
          slug !== "treasury_bonds" &&
          slug !== "crypto" &&
          slug !== "currencies" &&
          slug !== "investment_policy" &&
          slug !== "artwork" &&
          slug !== "precious_stones" &&
          slug !== "loans" &&
          slug !== "real_estate" && (
            <OtherAssetForm
              item={item}
              onSubmit={handleOtherAssetSubmit}
              onCancel={onClose}
              isPending={isPending || isUploading}
              currencyOptions={currencyOptions}
              onRemovedPhoto={(f) => otherAssetRemovedPhotos.current.add(f.url)}
              onRemovedContract={(f) =>
                otherAssetRemovedContracts.current.add(f.url)
              }
            />
          )}
      </>
    </FullscreenModal>
  );
};

export default EditInvestmentModal;
